import { useState, useRef, useEffect } from 'react';
import {
  IconAdd,
  IconGenStars,
  IconOpenInNew,
  IconArrowBackward,
  IconArrowForward,
  IconEdit, IconClear,
  IconTrash,
  IconContentCopy,
  IconCheck,
} from '@components/icons';
import { IconButton } from '@components/IconButton';
import { MenuSelect, type MenuSelectItem } from '@components/MenuSelect';
import { PlatformIcon, type Platform } from '@components/PlatformIcon';
import { Tooltip } from '@components/Tooltip';
import type { ApolloSequence } from '@/services/firebase/firestore';
import styles from './LeadTypeViewHeader.module.scss';

export interface LeadTypeViewHeaderProps {
  /** Type title */
  title?: string;

  /** Type slug */
  slug?: string;

  /** Last updated date */
  date?: string;

  /** Total leads count */
  totalLeads?: number;

  /** Website objectives list */
  websiteObjectives?: string[];

  /** Industry painpoints list */
  industryPainpoints?: string[];

  /** Whether to show the "Open in New" icon button next to the title */
  showOpenInNew?: boolean;

  /** Callback when "Open in New" button is clicked */
  onOpenInNew?: () => void;

  /** Label for the "Open in New" tooltip (e.g. domain name) */
  openInNewLabel?: string;

  /** Whether to show the metadata row (URLs count + platform) */
  showMetadata?: boolean;

  /** Total URLs count (shown when showMetadata is true) */
  totalUrls?: number;

  /** Platform type (shown when showMetadata is true) */
  platform?: Platform;

  /** Whether to show navigation buttons */
  showNavButtons?: boolean;

  /** Current index in the result set (1-based) */
  currentIndex?: number;

  /** Total count in the result set */
  totalCount?: number;

  /** Callback when previous button is clicked */
  onPrevious?: () => void;

  /** Callback when next button is clicked */
  onNext?: () => void;

  /** Whether to show the leads count */
  showLeads?: boolean;

  /** Whether to show the edit button next to the metadata chip */
  showEditButton?: boolean;

  /** Callback when edit button is clicked */
  onEdit?: () => void;

  /** Current sequence name (undefined = not set, shows "DEFAULT") */
  sequenceName?: string;

  /** Current sequence ID */
  sequenceId?: string;

  /** Available Apollo sequences for the dropdown */
  availableSequences?: ApolloSequence[];

  /** Callback when sequence selection changes (null = clear/reset to default) */
  onSequenceChange?: (sequenceId: string | null) => void;

  /** Whether to show the delete button next to the edit button */
  showDeleteButton?: boolean;

  /** Callback when delete button is clicked */
  onDelete?: () => void;

  /** Business type name (rendered as clickable link in slug row) */
  businessTypeName?: string;

  /** Callback when business type name is clicked (deep link to Generate Leads) */
  onBusinessTypeClick?: () => void;

  /**
   * List name — when set, prepends "{listName} · " in the subtitle row.
   * Renders as a clickable link when `onListClick` is provided (SC-2189).
   */
  listName?: string;

  /** Callback when the list name is clicked (deep-link to Manage Leads filtered by list) */
  onListClick?: () => void;

  /**
   * City name — when provided, structured-subtitle mode is enabled and the
   * component composes "{businessType} in {city}" itself (ignoring `slug`).
   * Requires opting into structured mode via `listName` or `onAddBusinessType`.
   */
  cityName?: string;

  /**
   * Callback to open the Change Business Type modal in "add" mode.
   * When set AND no `businessTypeName`, renders a "+" affordance in place of
   * the business-type link with tooltip "Set business type" (SC-2189).
   */
  onAddBusinessType?: () => void;

  /** Callback when the total leads count is clicked (deep link to Manage Leads filtered by business type) */
  onTotalLeadsClick?: () => void;

  /** Callback to open the Change Business Type modal (renders edit icon in slug row) */
  onChangeBusinessType?: () => void;

  /** Site ID for copy-to-clipboard (only for onboarded sites) */
  siteId?: string;

  /** Optional addon element rendered in the title row after copy-site-id (e.g. GIF preview icon) */
  titleAddon?: React.ReactNode;
}

export const LeadTypeViewHeader = ({
  title = 'Dental Clinic',
  slug = 'dental-clinic',
  date = 'Updated 15 Dec 12:34 PM',
  totalLeads = 187,
  websiteObjectives = [],
  industryPainpoints = [],
  showOpenInNew = false,
  onOpenInNew,
  openInNewLabel,
  showMetadata = false,
  totalUrls = 0,
  platform,
  showNavButtons = false,
  currentIndex = 1,
  totalCount = 1,
  onPrevious,
  onNext,
  showLeads = true,
  showEditButton = false,
  onEdit,
  sequenceName,
  sequenceId,
  availableSequences = [],
  onSequenceChange,
  showDeleteButton = false,
  onDelete,
  businessTypeName,
  onBusinessTypeClick,
  listName,
  onListClick,
  cityName,
  onAddBusinessType,
  onTotalLeadsClick,
  onChangeBusinessType,
  siteId,
  titleAddon,
}: LeadTypeViewHeaderProps) => {
  const [isChipHovered, setIsChipHovered] = useState(false);
  const [siteIdCopied, setSiteIdCopied] = useState(false);
  const [isSequenceDropdownOpen, setIsSequenceDropdownOpen] = useState(false);
  const sequenceDropdownRef = useRef<HTMLDivElement>(null);

  const isPreviousDisabled = currentIndex <= 1;
  const isNextDisabled = currentIndex >= totalCount;

  const hasSequence = !!sequenceId && !!sequenceName;
  const sequenceLabel = hasSequence ? sequenceName : 'DEFAULT';
  const sequenceTooltip = hasSequence
    ? 'Click to Modify Sequence for this Type'
    : 'Click to Set Sequence for this Type';

  // Build menu items: available sequences + clear option when set
  const sequenceMenuItems: MenuSelectItem[] = [
    ...(hasSequence
      ? [{ value: '__clear__', label: 'Reset to Default' }]
      : []),
    ...availableSequences.map((seq) => ({
      value: seq.sequence_id,
      label: seq.name,
    })),
  ];

  const handleSequenceSelect = (value: string) => {
    if (value === '__clear__') {
      onSequenceChange?.(null);
    } else {
      onSequenceChange?.(value);
    }
    setIsSequenceDropdownOpen(false);
  };

  const handleCopySiteId = () => {
    if (!siteId) return;
    navigator.clipboard.writeText(siteId).then(
      () => {
        setSiteIdCopied(true);
        setTimeout(() => setSiteIdCopied(false), 1500);
      },
      () => { /* clipboard unavailable */ }
    );
  };

  // Close dropdown on click outside
  useEffect(() => {
    if (!isSequenceDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        sequenceDropdownRef.current &&
        !sequenceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSequenceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSequenceDropdownOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Left side: Type info */}
        <div className={styles.info}>
          <div className={styles.titleRow}>
            <div className={styles.title}>{title}</div>
            {showOpenInNew && (
              <Tooltip
                text={openInNewLabel ? `Open ${openInNewLabel} in new tab` : 'Open in new tab'}
                position="bottom"
              >
                <IconButton
                  size="small"
                  aria-label="Open in new window"
                  onClick={onOpenInNew}
                  className={styles.openInNewButton}
                >
                  <IconOpenInNew size={14} />
                </IconButton>
              </Tooltip>
            )}
            {siteId && (
              <Tooltip text={siteIdCopied ? 'Site ID copied' : 'Copy Site ID'} position="bottom">
                <IconButton
                  size="small"
                  aria-label={siteIdCopied ? 'Site ID copied' : 'Copy Site ID'}
                  onClick={handleCopySiteId}
                  className={styles.openInNewButton}
                >
                  {siteIdCopied ? (
                    <IconCheck size={14} color="var(--color-semantic-success)" />
                  ) : (
                    <IconContentCopy size={14} color="var(--color-text-tertiary)" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {titleAddon}
          </div>
          <div className={styles.slugRow}>
            <div className={styles.slug}>
              {(() => {
                // Structured subtitle: any of listName / cityName / onAddBusinessType
                // opts into `{list} · {businessType | +} in {city}` composition.
                // Otherwise fall back to the legacy `slug` string (used by other consumers).
                const structured = Boolean(listName || cityName || onAddBusinessType);
                if (!structured) {
                  return businessTypeName && onBusinessTypeClick ? (
                    <>
                      <button
                        type="button"
                        className={styles.businessTypeLink}
                        onClick={onBusinessTypeClick}
                      >
                        {businessTypeName}
                      </button>
                      {slug?.replace(businessTypeName, '') || ''}
                    </>
                  ) : (
                    slug
                  );
                }

                const listPart =
                  listName && onListClick ? (
                    <button
                      type="button"
                      className={styles.businessTypeLink}
                      onClick={onListClick}
                    >
                      {listName}
                    </button>
                  ) : listName ? (
                    <span>{listName}</span>
                  ) : null;

                const businessTypePart = businessTypeName ? (
                  onBusinessTypeClick ? (
                    <button
                      type="button"
                      className={styles.businessTypeLink}
                      onClick={onBusinessTypeClick}
                    >
                      {businessTypeName}
                    </button>
                  ) : (
                    <span>{businessTypeName}</span>
                  )
                ) : onAddBusinessType ? (
                  <Tooltip text="Set business type" position="bottom">
                    <IconButton
                      size="small"
                      aria-label="Set business type"
                      onClick={onAddBusinessType}
                      className={styles.changeBusinessTypeButton}
                    >
                      <IconAdd size={12} />
                    </IconButton>
                  </Tooltip>
                ) : null;

                return (
                  <>
                    {listPart}
                    {listPart && businessTypePart ? <span className={styles.dot} /> : null}
                    {businessTypePart}
                    {cityName ? (
                      <span>
                        {listPart || businessTypePart ? ' in ' : ''}
                        {cityName}
                      </span>
                    ) : null}
                  </>
                );
              })()}
            </div>
            {onChangeBusinessType && (
              <Tooltip text="Change business type" position="bottom">
                <IconButton
                  size="small"
                  aria-label="Change business type"
                  onClick={onChangeBusinessType}
                  className={styles.changeBusinessTypeButton}
                >
                  <IconEdit size={12} />
                </IconButton>
              </Tooltip>
            )}
            {onSequenceChange && (
              <div className={styles.sequenceSelector} ref={sequenceDropdownRef}>
                <Tooltip text={sequenceTooltip}>
                <button
                  className={`${styles.sequenceTag} ${hasSequence ? styles.sequenceTagSet : ''}`}
                  onClick={() => setIsSequenceDropdownOpen((prev) => !prev)}
                  type="button"
                >
                  <span className={styles.sequenceTagLabel}>{sequenceLabel}</span>
                  {hasSequence && (
                    <span
                      className={styles.sequenceTagClear}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSequenceChange(null);
                        setIsSequenceDropdownOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          onSequenceChange(null);
                          setIsSequenceDropdownOpen(false);
                        }
                      }}
                      title="Reset to Default"
                    >
                      <IconClear size={12} />
                    </span>
                  )}
                </button>
                </Tooltip>
                {isSequenceDropdownOpen && sequenceMenuItems.length > 0 && (
                  <div className={styles.sequenceDropdown}>
                    <MenuSelect
                      items={sequenceMenuItems}
                      value={sequenceId ?? null}
                      onChange={handleSequenceSelect}
                      maxHeight={200}
                    />
                  </div>
                )}
              </div>
            )}
            {showMetadata && (
              <>
                <span className={styles.dot} />
                <div className={styles.urlsCount}>
                  <span>{totalUrls}</span>
                  <span>URLs</span>
                </div>
                {platform && platform !== 'unknown' && (
                  <>
                    <span className={styles.dot} />
                    <PlatformIcon platform={platform} className={styles.platformIcon} />
                  </>
                )}
              </>
            )}
          </div>
          <div className={styles.date}>{date}</div>
        </div>

        {/* Right side: Stats and chip */}
        <div className={styles.stats}>
          <div className={styles.topRow}>
            {showNavButtons && (
              <div className={styles.navButtons}>
                <IconButton
                  size="small"
                  aria-label="Previous lead"
                  onClick={onPrevious}
                  disabled={isPreviousDisabled}
                >
                  <IconArrowBackward size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Next lead"
                  onClick={onNext}
                  disabled={isNextDisabled}
                >
                  <IconArrowForward size={18} />
                </IconButton>
              </div>
            )}
            {showLeads &&
              (onTotalLeadsClick && (totalLeads ?? 0) > 0 ? (
                <Tooltip text="View leads on Manage Leads" position="bottom">
                  <button
                    type="button"
                    className={`${styles.leads} ${styles.leadsLink}`}
                    onClick={onTotalLeadsClick}
                    aria-label="View leads on Manage Leads"
                  >
                    <span className={styles.leadsCount}>{totalLeads}</span>
                    <span className={styles.leadsLabel}>Leads</span>
                  </button>
                </Tooltip>
              ) : (
                <div className={styles.leads}>
                  <span className={styles.leadsCount}>{totalLeads}</span>
                  <span className={styles.leadsLabel}>Leads</span>
                </div>
              ))}
          </div>

          <div className={styles.chipRow}>
            {(websiteObjectives.length > 0 || industryPainpoints.length > 0) && (
              <div
                className={styles.chip}
                onMouseEnter={() => setIsChipHovered(true)}
                onMouseLeave={() => setIsChipHovered(false)}
              >
                <div className={styles.chipSection}>
                  <span className={styles.chipValue}>{websiteObjectives.length}</span>
                  <span className={styles.chipLabel}>Website Objectives</span>
                </div>
                <div className={`${styles.chipSection} ${styles.chipSectionBordered}`}>
                  <span className={styles.chipValue}>{industryPainpoints.length}</span>
                  <span className={styles.chipLabel}>Industry Painpoints</span>
                </div>

                {/* Popover on hover */}
                {isChipHovered && (
                  <div className={styles.popover}>
                    <div className={styles.popoverContent}>
                      {/* Website Objectives section */}
                      <div className={styles.popoverSection}>
                        <div className={styles.popoverHeader}>
                          <IconGenStars size={18} />
                          <span className={styles.popoverHeaderText}>Website Objectives</span>
                        </div>
                        <div className={styles.popoverItems}>
                          {websiteObjectives.map((objective, index) => (
                            <div key={index} className={styles.popoverItem}>
                              {objective}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Industry Painpoints section */}
                      <div className={styles.popoverSection}>
                        <div className={styles.popoverHeader}>
                          <IconGenStars size={18} />
                          <span className={styles.popoverHeaderText}>Industry Painpoints</span>
                        </div>
                        <div className={styles.popoverItems}>
                          {industryPainpoints.map((painpoint, index) => (
                            <div key={index} className={styles.popoverItem}>
                              {painpoint}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {showEditButton && (
              <IconButton
                size="small"
                aria-label="Edit metadata"
                onClick={onEdit}
                className={styles.editButton}
              >
                <IconEdit size={14} />
              </IconButton>
            )}
            {showDeleteButton && (
              <IconButton
                size="small"
                aria-label="Delete business type"
                onClick={onDelete}
                className={styles.deleteButton}
              >
                <IconTrash size={14} />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
